<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import CortexConversionSettings from './CortexConversionSettings.vue';
import CortexConversionPrompts from './CortexConversionPrompts.vue';
import CortexModelSettings from './CortexModelSettings.vue';
import CortexConfidenceSettings from './CortexConfidenceSettings.vue';
import CortexSectionGuide from './CortexSectionGuide.vue';
import CortexUrlSettings from './CortexUrlSettings.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const sectionAliases = {
  confidence_summary: 'confidence',
};
const normalizeSection = section => sectionAliases[section] || section;

const sections = computed(() => [
  {
    key: 'models',
    icon: 'i-lucide-cpu',
    label: t('CORTEX.SETTINGS_GUIDE.MODELS.TITLE'),
    description: t('CORTEX.SETTINGS_GUIDE.MODELS.DESCRIPTION'),
  },
  {
    key: 'confidence',
    icon: 'i-lucide-shield-check',
    label: t('CORTEX_CONFIDENCE.TITLE'),
    description: t('CORTEX_CONFIDENCE.DESCRIPTION'),
  },
  {
    key: 'prompts',
    icon: 'i-lucide-message-square-text',
    label: t('CORTEX.SETTINGS_GUIDE.PROMPTS.TITLE'),
    description: t('CORTEX.SETTINGS_GUIDE.PROMPTS.DESCRIPTION'),
  },
  {
    key: 'conversions',
    icon: 'i-lucide-file-cog',
    label: t('CORTEX.SETTINGS_GUIDE.CONVERSIONS.TITLE'),
    description: t('CORTEX.SETTINGS_GUIDE.CONVERSIONS.DESCRIPTION'),
  },
  {
    key: 'urls',
    icon: 'i-lucide-link',
    label: t('CORTEX_URLS.TITLE'),
    description: t('CORTEX_URLS.DESCRIPTION'),
  },
]);

const initialSection = normalizeSection(route.query.section);
const activeSection = ref(
  sections.value.some(section => section.key === initialSection)
    ? initialSection
    : 'models'
);
const handleTabChanged = section => {
  activeSection.value = section.key;
  router.replace({ query: { ...route.query, section: section.key } });
};

watch(
  () => route.query.section,
  section => {
    const normalized = normalizeSection(section);
    if (sections.value.some(item => item.key === normalized)) {
      activeSection.value = normalized;
    }
  }
);
</script>

<template>
  <CortexSectionGuide
    :title="t('CORTEX.SETTINGS_GUIDE.TITLE')"
    :description="t('CORTEX.SETTINGS_GUIDE.DESCRIPTION')"
    :sections="sections"
    :active-section="activeSection"
    :first-row-size="3"
    @section-changed="handleTabChanged"
  >
    <CortexModelSettings v-if="activeSection === 'models'" />
    <CortexConfidenceSettings v-else-if="activeSection === 'confidence'" />
    <CortexConversionPrompts v-else-if="activeSection === 'prompts'" />
    <CortexConversionSettings v-else-if="activeSection === 'conversions'" />
    <CortexUrlSettings v-else />
  </CortexSectionGuide>
</template>
